import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { AuthClient, SetPasswordRequest } from '../../../clients/client.generated';
import { pageAnimation } from '@app/core/animations/element.animations';

@Component({
    selector: 'app-set-password',
    templateUrl: './set-password.component.html',
    styleUrls: ['./set-password.component.scss'],
    animations: [pageAnimation]
})
export class SetPasswordComponent implements OnInit, OnDestroy {
    form!: FormGroup;
    showNewPassword = false;
    showConfirmPassword = false;
    unknownErrorMessages: string[] = [];
    isSettingPassword = false;
    showResetRecovery = false;
    token = '';
    private onDestroy$ = new Subject<void>();

    constructor(
        private fb: FormBuilder,
        private route: ActivatedRoute,
        private router: Router,
        private authClient: AuthClient,
        private notification: NzNotificationService
    ) {}

    ngOnInit(): void {
        this.initializeForm();
        this.extractQueryParams();
    }

    ngOnDestroy(): void {
        this.onDestroy$.next();
        this.onDestroy$.complete();
        this.form?.reset();
    }

    private initializeForm(): void {
        this.form = this.fb.group({
            newPassword: ['', [Validators.required, Validators.minLength(8), this.passwordPolicyValidator]],
            confirmPassword: ['', [Validators.required]]
        }, { validators: this.passwordMatchValidator });
    }

    private extractQueryParams(): void {
        this.route.queryParams.pipe(takeUntil(this.onDestroy$)).subscribe(params => {
            this.token = params['token'] || '';
            this.clearErrors();

            if (!this.token) {
                this.unknownErrorMessages = ['The password reset link is invalid or expired. Please request a new link.'];
                this.showResetRecovery = true;
            }
        });
    }

    private passwordPolicyValidator(control: AbstractControl): { [key: string]: any } | null {
        const value = control.value;
        if (!value) {
            return null;
        }

        let criteriaCount = 0;
        const criteria = {
            lowerCase: /[a-z]/.test(value),
            upperCase: /[A-Z]/.test(value),
            numeric: /[0-9]/.test(value),
            specialChar: /[^a-zA-Z0-9]/.test(value)
        };

        if (criteria.lowerCase) {
            criteriaCount++;
        }
        if (criteria.upperCase) {
            criteriaCount++;
        }
        if (criteria.numeric) {
            criteriaCount++;
        }
        if (criteria.specialChar) {
            criteriaCount++;
        }

        if (criteriaCount < 3) {
            return { passwordPolicyFail: criteria };
        }

        return null;
    }

    private passwordMatchValidator(group: AbstractControl): { [key: string]: any } | null {
        const newPassword = group.get('newPassword');
        const confirmPassword = group.get('confirmPassword');

        if (!newPassword || !confirmPassword) {
            return null;
        }

        if (newPassword.value !== confirmPassword.value) {
            confirmPassword.setErrors({ passwordMismatch: true });
            return { passwordMismatch: true };
        } else {
            // Clear the error if passwords match
            if (confirmPassword.errors?.['passwordMismatch']) {
                delete confirmPassword.errors['passwordMismatch'];
                if (Object.keys(confirmPassword.errors).length === 0) {
                    confirmPassword.setErrors(null);
                }
            }
        }

        return null;
    }

    getErrorMessage(fieldName: string): string {
        const control = this.form.get(fieldName);
        if (!control || !control.errors) {
            return '';
        }

        if (control.errors['required']) {
            return `${this.getFieldDisplayName(fieldName)} is required`;
        }

        if (fieldName === 'newPassword') {
            if (control.errors['minlength']) {
                return 'Password must be at least 8 characters';
            }
            if (control.errors['passwordPolicyFail']) {
                return 'Password does not meet policy requirements';
            }
        }

        if (fieldName === 'confirmPassword' && control.errors['passwordMismatch']) {
            return 'Passwords do not match';
        }

        return 'Field is invalid';
    }

    private getFieldDisplayName(fieldName: string): string {
        const displayNames: { [key: string]: string } = {
            newPassword: 'New password',
            confirmPassword: 'Confirm password'
        };
        return displayNames[fieldName] || fieldName;
    }

    onSubmit(): void {
        if (!this.token || this.isSettingPassword) {
            return;
        }
        if (this.form.invalid) {
            // Mark all fields as touched to show validation errors
            Object.keys(this.form.controls).forEach(key => {
                this.form.get(key)?.markAsTouched();
            });
            return;
        }

        this.clearErrors();
        this.isSettingPassword = true;

        const request = new SetPasswordRequest({
            newPassword: this.form.value.newPassword,
            token: this.token
        });

        this.authClient.setPassword(request).pipe(
            takeUntil(this.onDestroy$)
        ).subscribe({
            next: (response) => {
                this.isSettingPassword = false;
                this.notification.success(
                    'Password Set Successfully',
                    'Your password has been set successfully. You can now log in with your new password.'
                );
                this.router.navigate(['/account/login']);
            },
            error: (error) => {
                this.isSettingPassword = false;
                this.handleError(error);
            }
        });
    }

    onRequestNewLink(): void {
        this.form.reset();
        this.router.navigate(['/account/reset-password']);
    }

    private clearErrors(): void {
        this.unknownErrorMessages = [];
        this.showResetRecovery = false;
    }

    private handleError(error: any): void {
        this.showResetRecovery = true;
        const status = error.statusCode ?? error.status;
        if (status === 400) {
            const errors = error.errors ?? error.error?.errors;
            const messages = errors && typeof errors === 'object'
                ? Object.values(errors).reduce<string[]>((all, value) =>
                    all.concat(Array.isArray(value) ? value.filter(item => typeof item === 'string') : []), [])
                : [];
            this.unknownErrorMessages = messages.length > 0 ? messages
                : ['Invalid or expired reset token. Please request a new password reset.'];
        } else {
            this.unknownErrorMessages = ['An error occurred while setting your password. Please try again.'];
        }
    }
}
